import React from 'react'
import { DEFAULT_THEME_COLORS } from 'css/theme'
import { PaymentMethod, PaymentProcessor } from 'models/ChargeIntent'
import { translate } from '../../../../../utils/functions/translate'

import { PaymentSelectionButtonProps } from '../components/payment-selection-button'
import DigitalWalletsButton from '../components/digital-wallets-button'
import { PaypalButton } from '../components/paypal-button'
import { PlaidButton } from '../components/plaid-button'
import {
  CreditCardSecondaryItems,
  CreditCardAccordionContent,
} from '../components/cc/credit-card-secondary-items'
import ConditionalCreditCardActionButton from '../components/cc/conditional-credit-card-action-button'
import { ACHAccordionContent } from '../components/ach/ach-accordion-content'
import ConditionalACHActionButton from '../components/ach/conditional-ach-action-button'
import { PaypalBraintree } from '../components/PaypalBraintree'

export interface PaymentMethodConfig {
  key: PaymentMethod
  processor?: PaymentProcessor
  icon: string
  label: string
  subtitle?: string
  secondaryItems: null | React.ComponentType
  accordionContent?: null | React.ComponentType
  footerCaption: string
  actionButtonText?: string
  actionButtonLogo?: string
  actionButtonLogoAltText?: string
  actionButtonColor: string
  actionButton: (props: PaymentSelectionButtonProps) => JSX.Element | null
  labelKey?: string
  order: number
}

export const paymentMethodMap: Record<PaymentMethod, string> = {
  Stripe: 'CREDIT_CARD',
  CC: 'CREDIT_CARD',
  PayPal_Commerce: 'PAYPAL_COMMERCE',
  PayPal: 'PAYPAL',
  Venmo: 'VENMO',
  ACH: 'ACH',
  Plaid: 'PLAID',
  DW: 'DW',
}

export const PAYMENT_METHOD_CONFIGS: { [key: string]: PaymentMethodConfig } = {
  CREDIT_CARD: {
    key: PaymentMethod.CC,
    icon: 'generic_cc',
    label: 'Card',
    labelKey: 'card',
    secondaryItems: CreditCardSecondaryItems,
    accordionContent: CreditCardAccordionContent,
    footerCaption: 'Enter your card details below',
    actionButtonColor: DEFAULT_THEME_COLORS['--theme-color--neutral-20'],
    actionButton: ConditionalCreditCardActionButton,
    actionButtonText: translate(
      'DonorInformationStep.footerButton.donateMessage.now',
      'Donate now',
    ),
    order: 1,
  },

  ACH: {
    key: PaymentMethod.ACH,
    icon: 'bank',
    label: 'Bank Transfer',
    labelKey: 'bank_transfer',
    secondaryItems: null,
    accordionContent: ACHAccordionContent,
    footerCaption: 'Enter your bank details below',
    actionButtonColor: DEFAULT_THEME_COLORS['--theme-color--neutral-20'],
    actionButton: ConditionalACHActionButton,
    actionButtonText: translate(
      'DonorInformationStep.footerButton.donateMessage.now',
      'Donate now',
    ),
    order: 4,
  },

  PLAID: {
    key: PaymentMethod.Plaid,
    icon: 'bank',
    label: 'Bank Transfer',
    labelKey: 'bank_transfer',
    subtitle: 'Instantly link your account with Plaid',
    secondaryItems: null,
    footerCaption: "Next you'll add your bank details",
    actionButtonColor: DEFAULT_THEME_COLORS['--theme-color__brand-primary'],
    actionButton: PlaidButton,
    actionButtonText: translate(
      'DonorInformationStep.footerButton.donateMessage.now',
      'Donate now',
    ),
    order: 4,
  },

  APPLEPAY: {
    key: PaymentMethod.DW,
    icon: 'applepay',
    label: 'Apple Pay',
    secondaryItems: null,
    footerCaption: "Next you'll open your ApplePay wallet",
    actionButtonLogo: 'applepay',
    actionButtonLogoAltText: 'Apple Pay',
    actionButtonColor: DEFAULT_THEME_COLORS['--theme-color--neutral-20'],
    actionButton: DigitalWalletsButton,
    order: 0,
  },

  GPAY: {
    key: PaymentMethod.DW,
    icon: 'gpay',
    label: 'Google Pay',
    secondaryItems: null,
    footerCaption: "Next you'll open your GooglePay wallet",
    actionButtonLogo: 'gpay',
    actionButtonLogoAltText: 'Google Pay',
    actionButtonColor: DEFAULT_THEME_COLORS['--theme-color--neutral-20'],
    actionButton: DigitalWalletsButton,
    order: 0,
  },

  PAYPAL_COMMERCE: {
    key: PaymentMethod.PayPal_Commerce,
    icon: 'paypal_icon',
    label: 'PayPal',
    secondaryItems: null,
    footerCaption: "Next you'll login to your PayPal account",
    actionButtonLogo: 'paypal',
    actionButtonLogoAltText: 'PayPal',
    actionButtonColor: DEFAULT_THEME_COLORS['--theme-color--brand-paypal'],
    actionButton: PaypalButton,
    order: 2,
  },

  PAYPAL: {
    key: PaymentMethod.PayPal,
    icon: 'paypal_icon',
    label: 'PayPal',
    secondaryItems: null,
    footerCaption: "Next you'll login to your PayPal account",
    actionButtonLogo: 'paypal',
    actionButtonLogoAltText: 'PayPal',
    actionButtonColor: DEFAULT_THEME_COLORS['--theme-color--brand-paypal'],
    actionButton: PaypalBraintree,
    order: 2,
  },

  VENMO: {
    key: PaymentMethod.Venmo,
    icon: 'venmo_icon',
    label: 'Venmo',
    secondaryItems: null,
    footerCaption: "Next you'll login to your Venmo account",
    actionButtonLogo: 'venmo',
    actionButtonLogoAltText: 'Venmo',
    actionButtonColor: DEFAULT_THEME_COLORS['--theme-color--brand-venmo'],
    actionButton: PaypalButton,
    order: 3,
  },
}
